/**
 * FN-FE-07 — Megrendelések UI (lista, szerkesztő, tételsor-szerkesztő, állapotok, clone)
 */
import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Badge from '../../components/ui/Badge';
import PageHeader from '../../components/ui/PageHeader';
import EmptyState from '../../components/ui/EmptyState';
import MoneyInput from '../../components/ui/MoneyInput';
import NumInput from '../../components/ui/NumInput';
import PartnerAutocomplete from '../../components/PartnerAutocomplete';
import ActionsDropdown from '../../components/ActionsDropdown';
import { ScannerButton } from '../../components/BarcodeScanner';
import { get, post } from '../../lib/api';
import { useToast } from '../../components/ui/Toast';
import Modal from '../../components/ui/Modal';
import PostIssueModal from '../../components/PostIssueModal';
import { useFKeyBar } from '../../components/layout/FKeyBar';
import ModulumBadge from '../../components/ui/ModulumBadge';

interface SalesOrder {
  id: string; order_number: string; partner_name?: string; partner_snapshot_json?: { name: string };
  order_date: string; status: string; subtotal_gross: string;
  network_order_id?: string | null;
}

const STATUS_MAP: Record<string, { label: string; variant: 'default' | 'info' | 'success' | 'error' }> = {
  draft: { label: 'Vázlat', variant: 'default' },
  confirmed: { label: 'Megerősítve', variant: 'info' },
  fulfilled: { label: 'Teljesítve', variant: 'success' },
  cancelled: { label: 'Törölve', variant: 'error' },
};

function formatPrice(f: string | number | null | undefined) { return Math.round(Number(f || 0)).toLocaleString('hu-HU') + ' Ft'; }
function formatDate(iso: string | null | undefined) { if (!iso) return '—'; try { return new Date(iso).toLocaleDateString('hu-HU', { year: 'numeric', month: 'short', day: 'numeric' }); } catch { return '—'; } }

export default function OrdersPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [orders, setOrders] = useState<SalesOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const [prefillPartner, setPrefillPartner] = useState<{ id: string; name: string } | null>(null);
  const [showDnModal, setShowDnModal] = useState<SalesOrder | null>(null);
  const [showDetail, setShowDetail] = useState<SalesOrder | null>(null);
  const [postIssue, setPostIssue] = useState<{ id: string; number: string; partnerId: string; partnerName: string } | null>(null);

  useFKeyBar([
    { key: 'F5', label: 'Új megrendelés', action: () => setShowEditor(true) },
    { key: 'F6', label: showDetail ? 'Bezárás' : 'Részletek', action: () => { if (showDetail) setShowDetail(null); } },
  ]);

  // Date filter state — default: current month
  const [dateFrom, setDateFrom] = useState(() => `${new Date().getFullYear()}-01-01`);
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0,10));

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pid = params.get('partnerId');
    const pname = params.get('partnerName');
    if (pid && pname) {
      setPrefillPartner({ id: pid, name: decodeURIComponent(pname) });
      setShowEditor(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  async function loadOrders() {
    setLoading(true);
    try {
      const q = search ? `?search=${encodeURIComponent(search)}` : '';
      const d = await get<any>(`/sales-orders${q}`);
      setOrders(d.data || d);
    } catch { /* */ }
    setLoading(false);
  }

  useEffect(() => { loadOrders(); }, []);
  useEffect(() => { const t = setTimeout(loadOrders, 300); return () => clearTimeout(t); }, [search]);

  const filtered = useMemo(() => {
    let list = orders;
    if (dateFrom) list = list.filter(o => (o.order_date || '').slice(0, 10) >= dateFrom);
    if (dateTo) list = list.filter(o => (o.order_date || '').slice(0, 10) <= dateTo);
    return list;
  }, [orders, dateFrom, dateTo]);

  // Reset page on filter change
  useEffect(() => { setPage(1); }, [dateFrom, dateTo, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div>
      <PageHeader title="Megrendelések" description={`${paginated.length}/${filtered.length} megrendelés`} actions={<Button onClick={() => setShowEditor(true)}>+ Új megrendelés</Button>} />

      <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap', alignItems: 'center' }}>
        <Input placeholder="Keresés sorszám, partner alapján..." value={search} onChange={e => setSearch(e.target.value)} style={{ flex: 1, minWidth: '200px' }} />
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="form-input" style={{ width: '140px', fontSize: '13px' }} title="Dátumtól" />
        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="form-input" style={{ width: '140px', fontSize: '13px' }} title="Dátumig" />
        <div style={{ display: 'flex', gap: '4px' }}>
          <button onClick={() => { const d = new Date(); setDateFrom(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`); setDateTo(d.toISOString().slice(0,10)); }} style={{ padding: '6px 10px', fontSize: '12px', border: '1px solid var(--line)', borderRadius: 'var(--r)', cursor: 'pointer', background: 'var(--white)', color: 'var(--ink)' }}>Ez a hónap</button>
          <button onClick={() => { const d = new Date(); d.setMonth(d.getMonth()-1); setDateFrom(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`); const last = new Date(d.getFullYear(), d.getMonth()+1, 0); setDateTo(last.toISOString().slice(0,10)); }} style={{ padding: '6px 10px', fontSize: '12px', border: '1px solid var(--line)', borderRadius: 'var(--r)', cursor: 'pointer', background: 'var(--white)', color: 'var(--ink)' }}>Előző hónap</button>
          <button onClick={() => { setDateFrom(''); setDateTo(''); }} style={{ padding: '6px 10px', fontSize: '12px', border: '1px solid var(--line)', borderRadius: 'var(--r)', cursor: 'pointer', background: 'var(--white)', color: 'var(--ink)' }}>Mind</button>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--slate)' }}>Betöltés...</div>
      ) : filtered.length === 0 ? (
        <EmptyState icon="📝" title="Még nincsenek megrendelések" description="Hozz létre egyet a + Új megrendelés gombbal." action={<Button size="sm" onClick={() => setShowEditor(true)}>+ Új megrendelés</Button>} />
      ) : (
        <div style={{ display: 'grid', gap: '10px' }}>
          {paginated.map(o => {
            const st = STATUS_MAP[o.status] || STATUS_MAP.draft;
            const partnerName = o.partner_name || o.partner_snapshot_json?.name || '—';
            return (
              <div key={o.id} className="card" style={{ cursor: 'pointer', padding: '14px 16px' }} onClick={() => setShowDetail(o)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                      {o.order_number}
                      {o.network_order_id && <span style={{ marginLeft: 8, fontSize: 10, background: 'var(--mist)', color: 'var(--orange)', fontWeight: 700, borderRadius: 4, padding: '1px 5px', textTransform: 'none' }}>🌐 Hálózati</span>}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--slate)', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {o.network_order_id && <ModulumBadge />}{partnerName}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }} onClick={e => e.stopPropagation()}>
                    <Badge variant={st.variant}>{st.label}</Badge>
                    <ActionsDropdown actions={[
                      { label: 'Megnyitás', icon: '👁', onClick: () => setShowDetail(o) },
                      ...(o.status === 'draft' ? [{ label: 'Megerősítés', icon: '✅', onClick: async () => {
                        try { await post(`/sales-orders/${o.id}/confirm`, {}); toast('Megerősítve.', 'success'); loadOrders(); } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
                      } }] : []),
                      ...(o.status === 'confirmed' ? [
                        { label: 'Szállítólevél', icon: '📦', onClick: () => setShowDnModal(o) },
                        { label: 'Számla', icon: '📄', onClick: () => {
                          const pName = o.partner_name || o.partner_snapshot_json?.name || '';
                          navigate(`/szamlak?fromOrder=${o.id}&partnerName=${encodeURIComponent(pName)}`);
                        } },
                        { separator: true, label: '', onClick: () => {} },
                        { label: 'Lezárás', icon: '🔒', onClick: async () => {
                          if (!confirm('Biztosan lezárod?')) return;
                          try { await post(`/sales-orders/${o.id}/fulfill`, {}); toast('Lezárva.', 'success'); loadOrders(); } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
                        } },
                      ] : []),
                      ...(o.status === 'fulfilled' ? [
                        { label: 'Újranyitás', icon: '🔓', onClick: async () => {
                          try { await post(`/sales-orders/${o.id}/reopen`, {}); toast('Újranyitva.', 'success'); loadOrders(); } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
                        } },
                      ] : []),
                      ...(o.status === 'draft' ? [{ separator: true, label: '', onClick: () => {} }, { label: 'Törlés', icon: '🗑', onClick: async () => {
                        if (!confirm('Biztosan törlöd?')) return;
                        try { await post(`/sales-orders/${o.id}/cancel`, {}); toast('Törölve.', 'success'); loadOrders(); } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
                      }, danger: true }] : []),
                    ]} />
                  </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', fontSize: '12px', color: 'var(--slate)' }}>
                  <span>Dátum: {formatDate(o.order_date)}</span>
                  <span style={{ marginLeft: 'auto', fontWeight: 700, fontSize: '14px', color: 'var(--navy)' }}>{formatPrice(o.subtotal_gross)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '16px', flexWrap: 'wrap' }}>
          <button disabled={page === 1} onClick={() => setPage(p => p - 1)} style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid var(--line)', borderRadius: 'var(--r)', cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.5 : 1, background: 'var(--white)' }}>← Előző</button>
          <span style={{ fontSize: '13px', color: 'var(--slate)' }}>{page} / {totalPages}</span>
          <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)} style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid var(--line)', borderRadius: 'var(--r)', cursor: page === totalPages ? 'not-allowed' : 'pointer', opacity: page === totalPages ? 0.5 : 1, background: 'var(--white)' }}>Következő →</button>
          <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }} className="form-input" style={{ width: '70px', fontSize: '12px' }}>
            <option value="10">10</option><option value="25">25</option><option value="50">50</option><option value="100">100</option>
          </select>
        </div>
      )}

      {showEditor && <OrderEditor onClose={() => { setShowEditor(false); setPrefillPartner(null); }} onSaved={() => { setShowEditor(false); setPrefillPartner(null); loadOrders(); }} initialPartner={prefillPartner} />}

      {showDetail && (
        <OrderDetailModal
          orderId={showDetail.id}
          onClose={() => { setShowDetail(null); loadOrders(); }}
          onCreateDn={(o) => { setShowDetail(null); setShowDnModal(o); }}
          onCreateInvoice={(o) => {
            setShowDetail(null);
            const pName = o.partner_name || o.partner_snapshot_json?.name || '';
            navigate(`/szamlak?fromOrder=${o.id}&partnerName=${encodeURIComponent(pName)}`);
          }}
        />
      )}

      {showDnModal && (
        <DeliveryNoteFromOrderModal
          order={showDnModal}
          onClose={() => setShowDnModal(null)}
          onSaved={(issued) => {
            setShowDnModal(null); loadOrders();
            if (issued) setPostIssue(issued);
          }}
        />
      )}

      {postIssue && (
        <PostIssueModal
          isOpen={true}
          onClose={() => setPostIssue(null)}
          docType="delivery-note"
          docId={postIssue.id}
          docNumber={postIssue.number}
          partnerId={postIssue.partnerId}
          partnerName={postIssue.partnerName}
        />
      )}
    </div>
  );
}

// ── Megrendelés szerkesztő — újraírt, scanner + görgethető tételek + hibaüzenet ──

interface LineItem {
  id: string; itemId: string; itemName: string; quantity: number; unitPrice: number; vatRate: number; discount: number;
  konfigInputs?: Record<string, string | number> | null;
  konfigDisplay?: string | null; // pl. "szín: antracit, 320×150"
  konfigParamsDisplay?: { nev: string; ertek: string | number }[] | null;
  isAutoItem?: boolean; // auto-tétel (szülőhöz tartozik)
  parentLocalId?: string | null; // helyi szülő sor ID (auto-tétel)
  autoQtyPerParent?: number; // 1 db szülőhöz tartozó mennyiség (szinkronhoz)
}

function OrderEditor({ onClose, onSaved, initialPartner }: { onClose: () => void; onSaved: () => void; initialPartner?: { id: string; name: string } | null }) {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<{ id: string; name: string; tax_number?: string } | null>(initialPartner || null);
  const [lines, setLines] = useState<LineItem[]>([]);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogItems, setCatalogItems] = useState<any[]>([]);

  // Cikk-katalógus betöltés
  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const q = catalogSearch ? `?search=${encodeURIComponent(catalogSearch)}` : '?pageSize=20';
        const data = await get<any>(`/items${q}`);
        setCatalogItems(Array.isArray(data) ? data : data?.data || []);
      } catch { setCatalogItems([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [catalogSearch]);

  const [konfigItem, setKonfigItem] = useState<any | null>(null);

  function addItemToLines(item: any) {
    if (item.isConfigurable) {
      setKonfigItem(item);
      return;
    }
    setLines(prev => [...prev, {
      id: String(Date.now()), itemId: item.id, itemName: item.name,
      quantity: 1, unitPrice: item.netUnitPrice || 0, vatRate: Number(item.vatRate) || 27, discount: 0,
    }]);
  }

  function addConfiguredItem(item: any, inputs: Record<string, string | number>, price: number, autoItems: { itemId: string; sku: string; name: string; qty: number; unitPrice: number; vatRate: number }[], paramsDisplay: string, paramsDisplayArray?: { nev: string; ertek: string | number }[]) {
    const parentId = String(Date.now());
    const newLines: LineItem[] = [
      {
        id: parentId, itemId: item.id, itemName: item.name,
        quantity: 1, unitPrice: price, vatRate: Number(item.vatRate) || 27, discount: 0,
        konfigInputs: inputs, konfigDisplay: paramsDisplay, konfigParamsDisplay: paramsDisplayArray || null,
      },
      ...autoItems.map((ai, i) => ({
        id: String(Date.now() + i + 1), itemId: ai.itemId, itemName: ai.name,
        quantity: ai.qty, unitPrice: ai.unitPrice, vatRate: ai.vatRate, discount: 0,
        isAutoItem: true, parentLocalId: parentId, autoQtyPerParent: ai.qty,
      })),
    ];
    setLines(prev => [...prev, ...newLines]);
    setKonfigItem(null);
  }

  function addItemByScan(code: string) {
    // Keresünk a katalógusban SKU alapján
    setCatalogSearch(code);
    // Ha pontosan 1 találat, auto-add
    setTimeout(async () => {
      try {
        const data = await get<any>(`/items?search=${encodeURIComponent(code)}&pageSize=1`);
        const items = Array.isArray(data) ? data : data?.data || [];
        if (items.length === 1) addItemToLines(items[0]);
      } catch { /* */ }
    }, 300);
  }

  function updateLine(id: string, field: keyof LineItem, value: string | number) {
    setLines(prev => {
      const updated = prev.map(l => l.id === id ? { ...l, [field]: value } : l);
      // Ha szülő mennyisége változik, auto-tételeket arányosan frissítjük
      if (field === 'quantity') {
        const parent = prev.find(l => l.id === id);
        if (parent && !parent.isAutoItem) {
          const newQty = Number(value);
          return updated.map(l =>
            l.parentLocalId === id && l.autoQtyPerParent != null
              ? { ...l, quantity: Math.max(1, Math.round(l.autoQtyPerParent * newQty)) }
              : l
          );
        }
      }
      return updated;
    });
  }

  function removeLine(id: string) {
    setLines(prev => prev.filter(l => l.id !== id));
  }

  const totals = useMemo(() => {
    let net = 0, vat = 0;
    for (const l of lines) {
      const lineNet = Math.round(l.quantity * l.unitPrice * (1 - l.discount / 100));
      const lineVat = Math.round(lineNet * l.vatRate / 100);
      net += lineNet; vat += lineVat;
    }
    return { net, vat, gross: net + vat };
  }, [lines]);

  function formatHuf(huf: number) { return Math.round(huf).toLocaleString('hu-HU') + ' Ft'; }

  async function handleSave() {
    if (!selectedPartner) { toast('Válassz partnert.', 'error'); return; }
    if (lines.length === 0) { toast('Adj hozzá legalább egy tételt.', 'error'); return; }

    setSaving(true);
    try {
      await post('/sales-orders', {
        partnerId: selectedPartner.id,
        lines: lines.map(l => ({
          itemId: l.itemId, quantity: l.quantity,
          unitPriceNet: l.unitPrice, discountPercent: l.discount,
          konfigParamsDisplay: l.konfigParamsDisplay || undefined,
        })),
      });
      onSaved();
    } catch (err: any) { toast(err.message || 'Mentés sikertelen.', 'error'); } finally { setSaving(false); }
  }

  return (
    <>
    <div className="overlay show" style={{ display: 'flex', padding: '20px' }} onClick={onClose}>
      <div className="modal modal-wide" style={{ maxHeight: 'calc(100vh - 40px)', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
        <div className="modal-h">
          <h3>+ Új megrendelés</h3>
          <button onClick={onClose} className="x">&times;</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
          {/* Partner */}
          <PartnerAutocomplete label="Partner" value={selectedPartner} onChange={setSelectedPartner} placeholder="Keress partnert..." required />

          <div style={{ display: 'flex', gap: '16px', marginTop: '16px', flexWrap: 'wrap' }}>
            {/* Bal: Cikk-kereső */}
            <div style={{ flex: '1 1 200px', minWidth: 0 }}>
              <label className="form-label">Cikk hozzáadása</label>
              <div style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>
                <input value={catalogSearch} onChange={e => setCatalogSearch(e.target.value)} placeholder="Keresés SKU / név..." className="form-input" style={{ fontSize: '12px' }} />
                <ScannerButton onScan={addItemByScan} size="sm" />
              </div>
              <div style={{ maxHeight: '250px', overflowY: 'auto', border: '1px solid var(--line)', borderRadius: 'var(--r)', background: 'var(--mist)' }}>
                {catalogItems.length === 0 ? (
                  <div style={{ padding: '16px', fontSize: '12px', color: 'var(--slate)', textAlign: 'center' }}>
                    {catalogSearch ? 'Nincs találat' : 'Hozz létre cikkeket a Cikkek menüben'}
                  </div>
                ) : catalogItems.map((item: any) => (
                  <button key={item.id} type="button" onClick={() => addItemToLines(item)}
                    className="dd-item" style={{ width: '100%', textAlign: 'left', fontSize: '12px', padding: '8px 10px' }}>
                    <span className="dot" style={{ background: 'var(--orange)' }} />
                    <span style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: '12px' }}>{item.name}</div>
                      <div style={{ color: 'var(--slate)', fontSize: '10px' }}>{item.sku} · {formatHuf(item.netUnitPrice || 0)} · {item.vatRate}%</div>
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Jobb: Tételek */}
            <div style={{ flex: '2 1 260px', minWidth: 0 }}>
              <label className="form-label">Tételek ({lines.length})</label>
              <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid var(--line)', borderRadius: 'var(--r)', background: 'var(--paper)' }}>
                {lines.length === 0 ? (
                  <div style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--slate)', fontSize: '13px' }}>
                    Válassz cikkeket a listából, vagy szkenneld be a vonalkódot.
                  </div>
                ) : lines.map(line => (
                  <div key={line.id} style={{ padding: line.isAutoItem ? '6px 12px 6px 28px' : '10px 12px', borderBottom: '1px solid var(--line-2)', background: line.isAutoItem ? 'var(--mist)' : undefined }}>
                    {/* Felső sor: név + törlés */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <div style={{ fontWeight: line.isAutoItem ? 500 : 700, fontSize: line.isAutoItem ? '11px' : '13px', color: line.isAutoItem ? 'var(--slate)' : 'var(--navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                        {line.isAutoItem ? '↳ ' : ''}{line.itemName}
                      </div>
                      {!line.isAutoItem && <button onClick={() => { const pid = line.id; setLines(prev => prev.filter(l => l.id !== pid && l.parentLocalId !== pid)); }} style={{ color: 'var(--red)', fontSize: '18px', border: 'none', background: 'none', cursor: 'pointer', padding: '2px 6px', flexShrink: 0 }}>×</button>}
                    </div>
                    {/* Konfig display */}
                    {line.konfigDisplay && <div style={{ fontSize: '10px', color: 'var(--slate)', marginBottom: 4 }}>{line.konfigDisplay}</div>}
                    {/* Alsó sor: mennyiség + ár + összeg */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      {!line.isAutoItem && <NumInput value={line.quantity} onChange={v => updateLine(line.id, 'quantity', v)} min={1} style={{ width: '56px', textAlign: 'center', padding: '4px' }} className="form-input" />}
                      {line.isAutoItem && <span style={{ fontSize: '11px', color: 'var(--slate)' }}>{line.quantity} ×</span>}
                      <span style={{ fontSize: '12px', color: 'var(--slate)' }}>{line.isAutoItem ? '' : '× '}{formatHuf(line.unitPrice)}</span>
                      <span style={{ fontSize: '11px', color: 'var(--slate)' }}>{line.vatRate}%</span>
                      <span style={{ marginLeft: 'auto', fontWeight: 700, fontSize: '13px', color: 'var(--navy)' }}>
                        {formatHuf((() => { const n = Math.round(line.quantity * line.unitPrice * (1 - line.discount / 100)); return n + Math.round(n * line.vatRate / 100); })())}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Összesítő */}
          <div style={{ marginTop: '16px', background: 'var(--mist)', borderRadius: 'var(--r)', padding: '14px 16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: 'var(--slate)', marginBottom: '4px' }}>
              <span>Nettó</span><span>{formatHuf(totals.net)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: 'var(--slate)', marginBottom: '4px' }}>
              <span>ÁFA</span><span>{formatHuf(totals.vat)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '16px', fontWeight: 800, color: 'var(--navy)', borderTop: '1px solid var(--line)', paddingTop: '8px', marginTop: '4px' }}>
              <span>Bruttó</span><span>{formatHuf(totals.gross)}</span>
            </div>
          </div>

        </div>

        {/* Footer */}
        <div style={{ padding: '14px 20px', borderTop: '1px solid var(--line)', display: 'flex', justifyContent: 'flex-end', gap: '9px', flexShrink: 0 }}>
          <Button variant="ghost" onClick={onClose}>Mégse</Button>
          <Button onClick={handleSave} loading={saving}>Megrendelés mentése</Button>
        </div>
      </div>
    </div>
    {konfigItem && (
      <KonfiguratorModal
        item={konfigItem}
        onClose={() => setKonfigItem(null)}
        onConfirm={(inputs, price, autoItems, display, paramsDisplayArray) => addConfiguredItem(konfigItem, inputs, price, autoItems, display, paramsDisplayArray)}
      />
    )}
    </>
  );
}

// ── Konfigurátor modal (kosárba tételkor) ──

interface KonfiguratorModalProps {
  item: any;
  onClose: () => void;
  onConfirm: (
    inputs: Record<string, string | number>,
    price: number,
    autoItems: { itemId: string; sku: string; name: string; qty: number; unitPrice: number; vatRate: number }[],
    display: string,
    paramsDisplayArray: { nev: string; ertek: string | number }[],
  ) => void;
}

function KonfiguratorModal({ item, onClose, onConfirm }: KonfiguratorModalProps) {
  const { toast } = useToast();
  const [konfigInputs, setKonfigInputs] = useState<any[]>([]);
  const [related, setRelated] = useState<any[]>([]);
  const [values, setValues] = useState<Record<string, string | number>>({});
  const [evalResult, setEvalResult] = useState<any | null>(null);
  const [evaluating, setEvaluating] = useState(false);
  const [selectedSuggested, setSelectedSuggested] = useState<Set<string>>(new Set());

  useEffect(() => {
    (async () => {
      try {
        const [inputs, rel] = await Promise.all([
          get<any[]>(`/items/${item.id}/konfig-inputs`),
          get<any[]>(`/items/${item.id}/related`),
        ]);
        setKonfigInputs(inputs || []);
        setRelated(rel || []);
        // default értékek
        const defaults: Record<string, string | number> = {};
        for (const inp of inputs || []) {
          if (inp.tipus === 'LISTA' && inp.opciok?.length) defaults[inp.kod] = inp.opciok[0];
          else if (inp.tipus === 'SZAM') defaults[inp.kod] = '';
          else defaults[inp.kod] = '';
        }
        setValues(defaults);
      } catch { /* */ }
    })();
  }, [item.id]);

  useEffect(() => {
    if (konfigInputs.length === 0) return;
    // Ne értékeljük ki, ha bármely SZAM mező üres (gépelés közben)
    const hasEmptySzam = konfigInputs.some(ki => ki.tipus === 'SZAM' && (values[ki.kod] === '' || values[ki.kod] === undefined));
    if (hasEmptySzam) { setEvalResult(null); return; }
    const t = setTimeout(async () => {
      setEvaluating(true);
      try {
        const r = await post<any>(`/items/${item.id}/evaluate`, { inputs: values });
        setEvalResult(r);
      } catch { /* */ }
      setEvaluating(false);
    }, 400);
    return () => clearTimeout(t);
  }, [values, konfigInputs.length]);

  function formatHuf(v: number) { return Math.round(v).toLocaleString('hu-HU') + ' Ft'; }

  async function handleConfirm() {
    if (!evalResult) { toast('Töltsd ki az összes mezőt.', 'error'); return; }
    if (evalResult.errors && evalResult.errors.length > 0) {
      toast('Javítsd a hibákat a konfigurálás előtt.', 'error');
      return;
    }
    // Összegyűjtjük a valódi auto-tételeket
    const autoItems: { itemId: string; sku: string; name: string; qty: number; unitPrice: number; vatRate: number }[] = [];
    for (const ai of (evalResult.addedItems || [])) {
      try {
        const itData = await get<any[]>(`/items?search=${encodeURIComponent(ai.itemSku)}&pageSize=1`);
        const itArr = Array.isArray(itData) ? itData : itData?.data || [];
        if (itArr.length > 0) {
          const it = itArr[0];
          autoItems.push({ itemId: it.id, sku: ai.itemSku, name: it.name, qty: ai.qty, unitPrice: it.netUnitPrice, vatRate: Number(it.vatRate) });
        }
      } catch { /* */ }
    }
    // Kötelező kapcsolódó termékek
    for (const rel of related.filter((r: any) => r.relationType === 'REQUIRED')) {
      const qty = rel.qtyFormula && evalResult.vars?.[rel.qtyFormula] != null
        ? evalResult.vars[rel.qtyFormula]
        : (rel.qtyFixed ?? 1);
      autoItems.push({ itemId: rel.relatedItemId, sku: rel.relatedSku, name: rel.relatedName, qty, unitPrice: rel.relatedNetUnitPrice ?? 0, vatRate: Number(rel.relatedVatRate ?? 27) });
    }
    // Javasolt (kiválasztott)
    for (const rel of related.filter((r: any) => r.relationType === 'SUGGESTED' && selectedSuggested.has(r.id))) {
      const qty = rel.qtyFormula && evalResult.vars?.[rel.qtyFormula] != null
        ? evalResult.vars[rel.qtyFormula]
        : (rel.qtyFixed ?? 1);
      autoItems.push({ itemId: rel.relatedItemId, sku: rel.relatedSku, name: rel.relatedName, qty, unitPrice: rel.relatedNetUnitPrice ?? 0, vatRate: Number(rel.relatedVatRate ?? 27) });
    }
    const display = konfigInputs.map(ki => `${ki.nev}: ${values[ki.kod] ?? ''}`).join(', ');
    const paramsDisplayArray = konfigInputs.map(ki => ({ nev: ki.nev, ertek: values[ki.kod] ?? '' }));
    onConfirm(values, evalResult.basePrice, autoItems, display, paramsDisplayArray);
  }

  const inputStyle = { fontSize: 13, padding: '6px 8px', border: '1px solid var(--line)', borderRadius: 6, background: '#fff', width: '100%' };
  const suggestedRel = related.filter((r: any) => r.relationType === 'SUGGESTED');
  const requiredRel = related.filter((r: any) => r.relationType === 'REQUIRED');

  return (
    <div className="overlay show" style={{ display: 'flex', padding: 20, zIndex: 300 }} onClick={onClose}>
      <div className="modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
        <div className="modal-h">
          <h3>Konfigurátor — {item.name}</h3>
          <button className="x" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-b" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {konfigInputs.length === 0 && <div style={{ fontSize: 13, color: 'var(--slate)', textAlign: 'center', padding: 16 }}>Nincs definiált konfigurátor input ennél a cikknél.</div>}
          {konfigInputs.map(ki => (
            <div key={ki.id}>
              <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--slate)', display: 'block', marginBottom: 4 }}>
                {ki.nev}{ki.egyseg ? ` (${ki.egyseg})` : ''}
              </label>
              {ki.tipus === 'LISTA' && (
                <select style={inputStyle} value={String(values[ki.kod] ?? '')} onChange={e => setValues(v => ({ ...v, [ki.kod]: e.target.value }))}>
                  {(ki.opciok || []).map((o: string) => <option key={o} value={o}>{o}</option>)}
                </select>
              )}
              {ki.tipus === 'SZAM' && (() => {
                const raw = values[ki.kod];
                const num = typeof raw === 'number' ? raw : parseFloat(String(raw));
                const rangeErr = !isNaN(num) && (
                  (ki.minErtek != null && num < ki.minErtek) ? `Minimum: ${ki.minErtek}` :
                  (ki.maxErtek != null && num > ki.maxErtek) ? `Maximum: ${ki.maxErtek}` : null
                );
                return (
                  <>
                    <input type="number" inputMode="decimal"
                      style={{ ...inputStyle, borderColor: rangeErr ? '#dc2626' : undefined }}
                      value={String(raw ?? '')}
                      onChange={e => setValues(prev => ({ ...prev, [ki.kod]: e.target.value === '' ? '' : parseFloat(e.target.value) }))}
                      placeholder={[ki.minErtek != null ? `min: ${ki.minErtek}` : null, ki.maxErtek != null ? `max: ${ki.maxErtek}` : null].filter(Boolean).join(' · ') || undefined} />
                    {rangeErr && <div style={{ fontSize: 11, color: '#dc2626', marginTop: 3 }}>{rangeErr}</div>}
                  </>
                );
              })()}
              {ki.tipus === 'SZOVEG' && (
                <input type="text" style={inputStyle} value={String(values[ki.kod] ?? '')}
                  onChange={e => setValues(v => ({ ...v, [ki.kod]: e.target.value }))} />
              )}
            </div>
          ))}

          {requiredRel.length > 0 && (
            <div style={{ background: 'var(--mist)', borderRadius: 6, padding: '10px 12px', fontSize: 12 }}>
              <div style={{ fontWeight: 700, color: 'var(--navy)', marginBottom: 4 }}>Kötelező kiegészítők (automatikusan hozzáadódnak):</div>
              {requiredRel.map((r: any) => <div key={r.id} style={{ color: 'var(--slate)' }}>• {r.relatedName} ({r.relatedSku})</div>)}
            </div>
          )}

          {suggestedRel.length > 0 && (
            <div style={{ background: '#f0fdf4', borderRadius: 6, padding: '10px 12px', fontSize: 12 }}>
              <div style={{ fontWeight: 700, color: '#15803d', marginBottom: 6 }}>Javasolt kiegészítők:</div>
              {suggestedRel.map((r: any) => (
                <label key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, cursor: 'pointer' }}>
                  <input type="checkbox" checked={selectedSuggested.has(r.id)}
                    onChange={e => setSelectedSuggested(prev => { const s = new Set(prev); e.target.checked ? s.add(r.id) : s.delete(r.id); return s; })} />
                  <span>{r.relatedName} ({r.relatedSku})</span>
                </label>
              ))}
            </div>
          )}

          {/* Hibák (csak CONDITIONAL_ERROR üzenetek, nem technikai formula-hibák) */}
          {(() => {
            const userErrors = (evalResult?.errors as string[] | undefined)?.filter(e => !/^[A-Z_]+:/.test(e)) ?? [];
            return userErrors.length > 0 ? (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px' }}>
                {userErrors.map((err, i) => (
                  <div key={i} style={{ color: '#dc2626', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                    <span style={{ flexShrink: 0 }}>⚠</span><span>{err}</span>
                  </div>
                ))}
              </div>
            ) : null;
          })()}

          {/* Élő ár + számított változók */}
          <div style={{ background: '#f8faff', border: '1px solid var(--line)', borderRadius: 8, padding: '10px 14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: evalResult?.vars && Object.keys(evalResult.vars).length > 0 ? 8 : 0 }}>
              <div>
                <div style={{ fontSize: 11, color: 'var(--slate)', marginBottom: 2 }}>Számított ár (nettó)</div>
                <div style={{ fontWeight: 800, fontSize: 18, color: 'var(--navy)' }}>
                  {evaluating ? '...' : evalResult ? `${Math.round(evalResult.basePrice).toLocaleString('hu-HU')} Ft` : `${Math.round(item.netUnitPrice || 0).toLocaleString('hu-HU')} Ft`}
                </div>
              </div>
              {evalResult?.m2OrQty && evalResult.m2OrQty !== 1 && (
                <div style={{ fontSize: 12, color: 'var(--slate)' }}>m²/qty: {Number(evalResult.m2OrQty).toFixed(4)}</div>
              )}
            </div>
            {evalResult?.vars && Object.keys(evalResult.vars).length > 0 && (
              <div style={{ borderTop: '1px solid var(--line-2)', paddingTop: 8, display: 'flex', flexWrap: 'wrap', gap: '4px 14px' }}>
                <span style={{ fontSize: 10, color: 'var(--slate)', textTransform: 'uppercase', letterSpacing: '.05em', width: '100%', marginBottom: 2 }}>Számított változók</span>
                {Object.entries(evalResult.vars as Record<string, number>).map(([k, v]) => (
                  <span key={k} style={{ fontSize: 12 }}>
                    <code style={{ background: '#e0f2fe', color: '#0369a1', padding: '1px 4px', borderRadius: 3, fontSize: 11 }}>{k}</code>
                    {' = '}
                    <strong>{typeof v === 'number' ? (Number.isInteger(v) ? v : Number(v).toFixed(4)) : v}</strong>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        <div style={{ padding: '12px 20px', borderTop: '1px solid var(--line)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="ghost" onClick={onClose}>Mégse</Button>
          <Button onClick={handleConfirm} disabled={
            (konfigInputs.length === 0 && related.length === 0) ||
            konfigInputs.some(ki => {
              if (ki.tipus !== 'SZAM') return false;
              const v = values[ki.kod];
              if (v === '' || v === undefined) return true;
              const n = typeof v === 'number' ? v : parseFloat(String(v));
              if (isNaN(n)) return true;
              if (ki.minErtek != null && n < ki.minErtek) return true;
              if (ki.maxErtek != null && n > ki.maxErtek) return true;
              return false;
            }) ||
            (evalResult?.errors as string[] | undefined)?.some(e => !/^[A-Z_]+:/.test(e))
          }>Hozzáadás a kosárhoz</Button>
        </div>
      </div>
    </div>
  );
}

// ── Szállítólevél készítés megrendelésből ──

interface DnOrderLine {
  id: string;
  description: string;
  ordered: number;
  delivered: number;
  remaining: number;
  unit: string;
  selected: boolean;
  deliverQty: number;
}

function DeliveryNoteFromOrderModal({ order, onClose, onSaved }: { order: SalesOrder; onClose: () => void; onSaved: (issued?: { id: string; number: string; partnerId: string; partnerName: string }) => void }) {
  const { toast } = useToast();
  const [lines, setLines] = useState<DnOrderLine[]>([]);
  const [deliveryDate, setDeliveryDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [showPrices, setShowPrices] = useState<boolean | null>(null); // null = tenant default
  const [saving, setSaving] = useState(false);
  const [loadingLines, setLoadingLines] = useState(true);

  // Default megjegyzés + show_prices
  useEffect(() => {
    (async () => {
      try {
        const profile = await get<any>('/company-profile');
        if (profile?.default_delivery_note_notes) setNotes(profile.default_delivery_note_notes);
        if (profile?.delivery_note_show_prices) setShowPrices(true);
      } catch { /* */ }
    })();
  }, []);

  // Megrendelés sorok betöltése
  useEffect(() => {
    (async () => {
      setLoadingLines(true);
      try {
        const data = await get<any>(`/sales-orders/${order.id}`);
        const orderLines = data.lines || data.line_items || [];
        setLines(orderLines.map((l: any) => {
          const ordered = Number(l.quantity);
          const delivered = Number(l.delivered_qty || 0);
          const remaining = ordered - delivered;
          return {
            id: l.id,
            description: l.item_name_snapshot || l.description || 'Tétel',
            ordered,
            delivered,
            remaining,
            unit: l.unit || 'db',
            selected: remaining > 0,
            deliverQty: remaining > 0 ? remaining : 0,
          };
        }));
      } catch { setLines([]); }
      setLoadingLines(false);
    })();
  }, [order.id]);

  function toggleAll(checked: boolean) {
    setLines(prev => prev.map(l => l.remaining > 0 ? { ...l, selected: checked, deliverQty: checked ? l.remaining : 0 } : l));
  }

  function toggleLine(lineId: string) {
    setLines(prev => prev.map(l => {
      if (l.id !== lineId || l.remaining <= 0) return l;
      const newSel = !l.selected;
      return { ...l, selected: newSel, deliverQty: newSel ? l.remaining : 0 };
    }));
  }

  const selectedLines = lines.filter(l => l.selected && l.deliverQty > 0);
  const allSelected = lines.filter(l => l.remaining > 0).length > 0 && lines.filter(l => l.remaining > 0).every(l => l.selected);

  async function handleSave() {
    if (selectedLines.length === 0) { toast('Válassz ki legalább egy tételt.', 'error'); return; }
    setSaving(true);
    try {
      const result = await post<any>(`/sales-orders/${order.id}/create-delivery-note`, {
        deliveryDate,
        notes: notes || undefined,
        showPrices: showPrices,
        lines: selectedLines.map(l => ({ salesOrderLineId: l.id, quantity: l.deliverQty })),
      });
      const pName = order.partner_name || order.partner_snapshot_json?.name || '';
      onSaved({
        id: result.id,
        number: result.delivery_note_number || '',
        partnerId: result.partner_id || '',
        partnerName: pName,
      });
    } catch (e: any) {
      toast(e.message || 'Mentés sikertelen.', 'error');
    } finally { setSaving(false); }
  }

  const partnerName = order.partner_name || order.partner_snapshot_json?.name || '';

  return (
    <Modal isOpen={true} onClose={onClose} title={`Szállítólevél — ${order.order_number.toUpperCase()}`} size="lg">
      <div className="space-y-4" style={{ maxHeight: '75vh', overflowY: 'auto' }}>

        {/* Info */}
        <div style={{ display: 'flex', gap: '20px', fontSize: '13px', color: 'var(--slate)', background: 'var(--mist)', padding: '10px 14px', borderRadius: 'var(--r)' }}>
          <span><b>Megrendelés:</b> {order.order_number.toUpperCase()}</span>
          <span><b>Partner:</b> {partnerName}</span>
        </div>

        <Input label="Szállítás dátuma *" type="date" value={deliveryDate} onChange={e => setDeliveryDate(e.target.value)} />

        {/* Tételek */}
        {loadingLines ? (
          <div style={{ padding: '20px', textAlign: 'center', color: 'var(--slate)' }}>Tételek betöltése...</div>
        ) : lines.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', color: 'var(--slate)' }}>Nincs tétel a megrendelésben.</div>
        ) : (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label className="form-label" style={{ margin: 0 }}>Tételek kiválasztása</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', color: 'var(--ink)', fontWeight: 500 }}>
                <input type="checkbox" checked={allSelected} onChange={e => toggleAll(e.target.checked)}
                  style={{ width: '16px', height: '16px', accentColor: 'var(--navy)' }} />
                Összes tétel
              </label>
            </div>

            <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--r)', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>

              {lines.map((l, idx) => {
                const isFull = l.remaining <= 0;
                return (
                  <div key={l.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
                      borderTop: idx > 0 ? '1px solid var(--line-2)' : 'none',
                      background: isFull ? 'var(--white)' : l.selected ? 'var(--mist)' : 'var(--white)',
                      opacity: isFull ? 0.45 : 1,
                      padding: '10px 12px',
                    }}
                  >
                    <div style={{ flexShrink: 0 }}>
                      {!isFull ? (
                        <input type="checkbox" checked={l.selected} onChange={() => toggleLine(l.id)}
                          style={{ width: '18px', height: '18px', accentColor: 'var(--navy)', cursor: 'pointer' }} />
                      ) : <span style={{ fontSize: '16px' }} title="Kész">✅</span>}
                    </div>
                    <div style={{ flex: 1, minWidth: '120px', cursor: isFull ? 'default' : 'pointer' }} onClick={() => !isFull && toggleLine(l.id)}>
                      <div style={{ fontWeight: 600, fontSize: '13px', color: l.selected && !isFull ? 'var(--navy)' : 'var(--slate)' }}>
                        {l.description}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--slate)', marginTop: '2px' }}>
                        Rendelve: {l.ordered} · Szállítva: {l.delivered} · Hátralék: <b>{isFull ? 0 : l.remaining}</b> {l.unit}
                        {isFull && <Badge variant="success" style={{ marginLeft: '6px', fontSize: '9px' }}>Kész</Badge>}
                      </div>
                    </div>
                    {!isFull && l.selected && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                        <NumInput value={l.deliverQty}
                          onChange={v => setLines(prev => prev.map(x => x.id === l.id ? { ...x, deliverQty: v } : x))}
                          min={0.01} max={l.remaining} step="any"
                          className="form-input"
                          style={{ width: '70px', textAlign: 'center', padding: '6px', fontSize: '14px', fontWeight: 600 }}
                        />
                        <span style={{ fontSize: '12px', color: 'var(--slate)' }}>{l.unit}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div style={{ fontSize: '12px', color: 'var(--slate)', marginTop: '6px' }}>
              {selectedLines.length} / {lines.filter(l => l.remaining > 0).length} tétel kiválasztva
            </div>
          </div>
        )}

        {/* Megjegyzés */}
        <div>
          <label className="form-label">Megjegyzés</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className="form-input"
            placeholder="Szállítólevélhez fűzött megjegyzés..." />
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px' }}>
          <input type="checkbox" checked={showPrices === true} onChange={e => setShowPrices(e.target.checked)}
            style={{ width: '16px', height: '16px', accentColor: 'var(--navy)' }} />
          <span>Árak megjelenítése a szállítólevélen</span>
        </label>

      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '12px', borderTop: '1px solid var(--line)', marginTop: '12px' }}>
        <div style={{ fontSize: '12px', color: 'var(--slate)' }}>
          {selectedLines.length > 0 && `${selectedLines.length} tétel a szállítólevélen`}
        </div>
        <div style={{ display: 'flex', gap: '9px' }}>
          <Button variant="ghost" onClick={onClose}>Mégse</Button>
          <Button onClick={handleSave} loading={saving} disabled={selectedLines.length === 0}>
            Szállítólevél létrehozása
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Megrendelés részletek modal ──

const DN_STATUS: Record<string, { label: string; variant: 'default' | 'info' | 'success' | 'error' | 'warning' }> = {
  draft: { label: 'Vázlat', variant: 'default' }, issued: { label: 'Kiállítva', variant: 'info' },
  cancelled: { label: 'Törölve', variant: 'error' }, storno: { label: 'Sztornózva', variant: 'warning' },
};
const INV_STATUS: Record<string, { label: string; variant: 'default' | 'info' | 'success' | 'error' | 'warning' }> = {
  draft: { label: 'Vázlat', variant: 'default' }, issued: { label: 'Kiállítva', variant: 'info' },
  paid: { label: 'Fizetve', variant: 'success' }, cancelled: { label: 'Sztornó', variant: 'error' },
};
const INV_TYPE: Record<string, string> = { normal: 'Számla', proforma: 'Díjbekérő', cancellation: 'Sztornó', corrective: 'Helyesbítő' };

function OrderDetailModal({ orderId, onClose, onCreateDn, onCreateInvoice }: {
  orderId: string;
  onClose: () => void;
  onCreateDn: (order: SalesOrder) => void;
  onCreateInvoice: (order: SalesOrder) => void;
}) {
  const { toast } = useToast();
  const [order, setOrder] = useState<any>(null);
  const [related, setRelated] = useState<{ deliveryNotes: any[]; invoices: any[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  async function load() {
    setLoading(true);
    setLoadError('');
    try {
      const [o, r] = await Promise.all([
        get<any>(`/sales-orders/${orderId}`),
        get<any>(`/sales-orders/${orderId}/related`).catch(() => ({ deliveryNotes: [], invoices: [] })),
      ]);
      setOrder(o);
      setRelated(r);
    } catch (e: any) {
      console.error('[OrderDetail] Load error:', e);
      setLoadError(e.message || 'Betöltés sikertelen.');
    }
    setLoading(false);
  }

  useEffect(() => { load(); }, [orderId]);

  if (loading) {
    return (
      <Modal isOpen={true} onClose={onClose} title="Megrendelés" size="lg">
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--slate)' }}>Betöltés...</div>
      </Modal>
    );
  }

  if (!order || loadError) {
    return (
      <Modal isOpen={true} onClose={onClose} title="Megrendelés" size="lg">
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--red)' }}>
          {loadError || 'A megrendelés nem található.'}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '12px' }}>
          <Button variant="ghost" onClick={onClose}>Bezárás</Button>
        </div>
      </Modal>
    );
  }

  const st = STATUS_MAP[order.status] || STATUS_MAP.draft;
  const partnerName = order.partner_name || '';
  const lines = order.lines || [];
  const dns = related?.deliveryNotes || [];
  const invs = related?.invoices || [];

  return (
    <Modal isOpen={true} onClose={onClose} title={`Megrendelés: ${(order.order_number || '').toUpperCase()}`} size="lg">
      <div className="space-y-4">

        {/* Fejléc infók */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '12px' }}>
          <InfoBlock label="Partner" value={partnerName} />
          <InfoBlock label="Dátum" value={formatDate(order.order_date)} />
          <InfoBlock label="Státusz" badge={<Badge variant={st.variant}>{st.label}</Badge>} />
          <InfoBlock label="Bruttó" value={formatPrice(order.subtotal_gross)} bold />
        </div>

        {order.notes && (
          <div style={{ background: 'var(--mist)', borderRadius: 'var(--r)', padding: '10px 14px', fontSize: '13px', color: 'var(--ink)' }}>
            <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--slate)', fontWeight: 700, marginBottom: '4px' }}>Megjegyzés</div>
            {order.notes}
          </div>
        )}

        {/* Tételek */}
        <div>
          <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--slate)', fontWeight: 700, marginBottom: '8px' }}>
            Tételek ({lines.length})
          </div>
          <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--r)', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            {lines.map((l: any, i: number) => (
              <div key={l.id || i} style={{
                padding: '10px 14px',
                borderTop: i > 0 ? '1px solid var(--line-2)' : 'none',
                display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px',
              }}>
                <div style={{ flex: 1, minWidth: '140px' }}>
                  <div style={{ fontWeight: 600, fontSize: '13px', color: 'var(--navy)' }}>{l.item_name_snapshot || 'Tétel'}</div>
                  {l.item_sku_snapshot && <div style={{ fontSize: '10px', color: 'var(--slate)' }}>{l.item_sku_snapshot}</div>}
                  {l.konfig_params_display && l.konfig_params_display.length > 0 && (
                    <div style={{ fontSize: '11px', color: 'var(--slate)', marginTop: '2px', display: 'flex', flexWrap: 'wrap', gap: '2px 10px' }}>
                      {l.konfig_params_display.map((p: any, pi: number) => (
                        <span key={pi}><span style={{ fontWeight: 500 }}>{p.nev}:</span> {p.ertek}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--slate)', whiteSpace: 'nowrap' }}>
                  {Number(l.quantity).toLocaleString('hu-HU')} {l.unit || 'db'} × {formatPrice(l.unit_price_net)}
                </div>
                {Number(l.discount_percent) > 0 && (
                  <Badge variant="warning" style={{ fontSize: '9px' }}>-{l.discount_percent}%</Badge>
                )}
                <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--navy)', marginLeft: 'auto', whiteSpace: 'nowrap' }}>
                  {formatPrice(l.line_gross)}
                </div>
              </div>
            ))}
          </div>

          {/* Összesítő */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
            <div style={{ width: '220px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--slate)', marginBottom: '2px' }}>
                <span>Nettó</span><span>{formatPrice(order.subtotal_net)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--slate)', marginBottom: '4px' }}>
                <span>ÁFA</span><span>{formatPrice(order.subtotal_vat)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '15px', fontWeight: 800, color: 'var(--navy)', borderTop: '2px solid var(--navy)', paddingTop: '6px' }}>
                <span>Bruttó</span><span>{formatPrice(order.subtotal_gross)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Kapcsolódó szállítólevelek */}
        {dns.length > 0 && (
          <div>
            <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--slate)', fontWeight: 700, marginBottom: '8px' }}>
              Szállítólevelek ({dns.length})
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {dns.map((dn: any) => {
                const dst = DN_STATUS[dn.status] || DN_STATUS.draft;
                return (
                  <div key={dn.id} style={{
                    display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
                    padding: '8px 12px', background: 'var(--mist)', borderRadius: 'var(--r)',
                    fontSize: '13px',
                  }}>
                    <span style={{ fontWeight: 700, textTransform: 'uppercase', color: 'var(--navy)', letterSpacing: '0.3px' }}>
                      📦 {dn.delivery_note_number}
                    </span>
                    <Badge variant={dst.variant}>{dst.label}</Badge>
                    <span style={{ color: 'var(--slate)', fontSize: '12px', marginLeft: 'auto' }}>
                      {formatDate(dn.delivery_date)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Kapcsolódó számlák */}
        {invs.length > 0 && (
          <div>
            <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--slate)', fontWeight: 700, marginBottom: '8px' }}>
              Számlák ({invs.length})
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {invs.map((inv: any) => {
                const ist = INV_STATUS[inv.status] || INV_STATUS.draft;
                const typeLabel = INV_TYPE[inv.type] || 'Számla';
                return (
                  <div key={inv.id} style={{
                    display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
                    padding: '8px 12px', background: 'var(--mist)', borderRadius: 'var(--r)',
                    fontSize: '13px',
                  }}>
                    <span style={{ fontWeight: 700, textTransform: 'uppercase', color: 'var(--navy)', letterSpacing: '0.3px' }}>
                      📄 {inv.invoice_number || 'Vázlat'}
                    </span>
                    <Badge variant={ist.variant}>{ist.label}</Badge>
                    <span style={{ fontSize: '11px', color: 'var(--slate)' }}>{typeLabel}</span>
                    <span style={{ fontWeight: 600, fontSize: '13px', color: 'var(--navy)', marginLeft: 'auto' }}>
                      {formatPrice(Math.round(Number(inv.subtotal_gross) / 100))}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {dns.length === 0 && invs.length === 0 && order.status === 'confirmed' && (
          <div style={{ textAlign: 'center', padding: '16px', color: 'var(--slate)', fontSize: '13px', background: 'var(--mist)', borderRadius: 'var(--r)' }}>
            Még nincs szállítólevél vagy számla ehhez a megrendeléshez.
          </div>
        )}
      </div>

      {/* Akciók */}
      <div style={{ paddingTop: '14px', borderTop: '1px solid var(--line)', marginTop: '14px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'flex-end' }}>
          {order.status === 'confirmed' && (
            <>
              <Button onClick={() => onCreateDn(order)} style={{ flex: '1 1 auto', minWidth: '140px' }}>
                📦 Szállítólevél
              </Button>
              <Button variant="ghost" onClick={() => onCreateInvoice(order)} style={{ flex: '1 1 auto', minWidth: '120px' }}>
                📄 Számla
              </Button>
              <Button variant="ghost" onClick={async () => {
                if (!confirm('Biztosan lezárod a megrendelést?')) return;
                try { await post(`/sales-orders/${order.id}/fulfill`, {}); toast('Megrendelés lezárva.', 'success'); load(); } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
              }} style={{ flex: '0 0 auto' }}>🔒 Lezárás</Button>
            </>
          )}
          {order.status === 'fulfilled' && (
            <Button variant="ghost" onClick={async () => {
              try { await post(`/sales-orders/${order.id}/reopen`, {}); toast('Megrendelés újranyitva.', 'success'); load(); } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
            }}>🔓 Újranyitás</Button>
          )}
          {order.status === 'draft' && (
            <Button onClick={async () => {
              try { await post(`/sales-orders/${order.id}/confirm`, {}); toast('Megrendelés megerősítve.', 'success'); load(); } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
            }}>✅ Megerősítés</Button>
          )}
          <Button variant="ghost" onClick={onClose}>Bezárás</Button>
        </div>
      </div>
    </Modal>
  );
}

function InfoBlock({ label, value, bold, badge }: { label: string; value?: string; bold?: boolean; badge?: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--slate)', fontWeight: 700, marginBottom: '3px' }}>{label}</div>
      {badge || <div style={{ fontSize: '14px', fontWeight: bold ? 700 : 500, color: 'var(--navy)' }}>{value || '—'}</div>}
    </div>
  );
}
